import m from './module';
import { MissingField, FieldInstance } from '../model';
import {
  getProvisionalIdentifierForType
} from '../util';
import { createError } from '../error';
import { cloneDeep } from "lodash";

const type = 'FieldInstance';

class FieldInstanceService {
  constructor(
    $q,
    $log,
    FieldService,
    LogicGroupService,
    ValidationService,
    ValidationTypeService,
    FieldTypeService,
    DataSyncService,
    LogService,
    RoleVariableService,
    LocationService
  ) {
    "ngInject";
    this.$q = $q;
    this.$log = $log;
    this.FieldService = FieldService;
    this.LogicGroupService = LogicGroupService;
    this.ValidationService = ValidationService;
    this.ValidationTypeService = ValidationTypeService;
    this.FieldTypeService = FieldTypeService;
    this.DataSyncService = DataSyncService;
    this.LogService = LogService;
    this.RoleVariableService = RoleVariableService;
    this.LocationService = LocationService;
  }

  create(data, config = null) {
    const promises = [];

    data = data || {};

    data.id = data.id || getProvisionalIdentifierForType('field_instance');
    data.events_custom_id = data.events_custom_id || null;
    data.events_custom_forms_id = data.events_custom_forms_id || null;
    data.events_custom_field_blocks_id = data.events_custom_field_blocks_id || null;
    data.field_block_width = data.field_block_width ? parseInt(data.field_block_width) : 12;

    const recapDefinitionId = data.recapDefinitionId || data.events_recaps_id || null;
    const fieldInstanceId = data.id || null;

    /**
     * Load field for this field instance.
     * If events_custom_id is present, load field by ID.
     * Otherwise, require that a field object is passed in.
     */
    let fieldPromise;
    if (data.field) {
      fieldPromise = this.$q.when(this.FieldService.create(data.field));
    } else if (data.events_custom_id) {
      // Load field
      let fieldId = data.events_custom_id;
      fieldPromise =
        this.$q
          .when(this.FieldService.getById(fieldId, config))
          .then(field => {
            if (!field) {
              return this.$q.reject(createError({
                error: 'FieldServiceReturnedEmptyField',
              }));
            }
            return field;
          })
          .catch(err => {
            this.LogService.error(`FieldInstanceService.create() Unable to load field ${fieldId} for field instance ${fieldInstanceId} in recap definition ${recapDefinitionId}. err:`, err, "data:", data);
            return this.$q.reject(createError({
              error: 'UnableToLoadFieldForFieldInstance',
              message: `Error loading field ${data.events_custom_id} for field instance ${fieldInstanceId} in recap definition ${recapDefinitionId}`,
              wrapped: err,
            }));
          });
    } else {
      this.LogService.warn(`FieldInstanceService.create() missing field ID for field instance ${fieldInstanceId}. Data:`, data);
      return this.$q.reject(
        new MissingField('Field is required for FieldInstance', data)
      );
    }

    promises.push(
      fieldPromise
        .then(field => {
          if (!field) {
            return this.$q.reject(createError({
              error: 'FieldRequiredForFieldInstance',
              description: 'Field is required for field instance',
            }));
          }
          data.field = field;
          const sField = cloneDeep(field);
          data.enabledValidationTypes = sField.enabledValidationTypes;
          data.nonTargetEnabledValidationTypes = sField.enabledValidationTypes.filter(type => !type.target);
          data.hasEnabledValidationTypes = sField.enabledValidationTypes.length;
        })
    );

    const logicGroups = data.logic_groups || [];
    promises.push(
      this.$q
        .all(
          logicGroups.map(group => this.$q.when(this.LogicGroupService.create(group)))
        )
        .then(logicGroups => data.logic_groups = logicGroups)
    );

    const validations = data.validations || [];
    promises.push(
      this.$q.all(
        validations.map(validation => this.$q.when(this.ValidationService.create(validation)))
      )
        .then(validations => data.validations = validations)
    );


    return this.$q.all(promises)
      .then(() => {
        const fieldInstance = new FieldInstance(data);
        if (data.id) {
          return this.DataSyncService.setObjectById(type, data.id, fieldInstance);
        } else {
          return fieldInstance;
        }
      })
      .catch(err => {
        this.LogService.error(`FieldInstanceService.create() Error creating field instance ${fieldInstanceId}:`, err);
        return this.$q.reject(err);
      })
  }

  serialize(fieldInstance) {
    const fieldId = fieldInstance.field ? fieldInstance.field.id : fieldInstance.events_custom_id;
    const evaluationSettings = fieldInstance.field ? fieldInstance.field.evaluationSettings : null;
    return {
      ...(evaluationSettings ? { field: evaluationSettings } : {}),
      id: fieldInstance.id,
      events_custom_id: fieldId,
      events_custom_forms_id: fieldInstance.events_custom_forms_id,
      events_custom_field_blocks_id: fieldInstance.events_custom_field_blocks_id,
      display_order: fieldInstance.display_order,
      field_block_width: fieldInstance.field_block_width || 12,
      logic_groups: fieldInstance.logic_groups.map(group => this.LogicGroupService.serialize(group)),
      validations: fieldInstance.validations.map(validation => this.ValidationService.serialize(validation)),
      location_variable_ids: fieldInstance.location_variables.map(lv => lv.id),
      role_variables: fieldInstance.role_variables.map(rv => this.RoleVariableService.serialize({ ...rv, events_custom_instances_id: fieldInstance.id })),
      locations: fieldInstance.locations.map(loc => this.LocationService.serialize({ ...loc, events_custom_instances_id: fieldInstance.id })),
      merchandising_activities_ids: (fieldInstance.merchandising_activities || []).map(a => a.id),
      default_options: fieldInstance.default_options,
    };
  }

  getById(id) {
    return this.DataSyncService.getLocalObjectById(type, id);
  }

  _getEnabledValidatonsByType(type) {
    return this.ValidationTypeService.getAll()
      .then(allValidationTypes => {
        return this.$q.when(this.ValidationTypeService.enabledKeysForType(type))
          .then((enabledValidatonKeys) => {
            return allValidationTypes.filter(vtype => enabledValidatonKeys.includes(vtype.key));
          })
      })
      .catch(err => {
        this.LogService.error("error loading validation types:", err);
        return this.$q.reject(err);
      });
  }
}

export default FieldInstanceService;

m.service('FieldInstanceService', FieldInstanceService);
